import { getCombinedToken } from '@local/login';
import type { BaseQueryFn, FetchArgs } from '@reduxjs/toolkit/query/react';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { getApiEndpoint } from 'src/utils/api-endpoints';
import { getIsProduction } from 'src/utils/get-is-production';

import { ApiEndpointTags } from './imago-mp.types';
import type {
    ActiveVersionsPageResult,
    ActiveVersionsQueryArg,
    Addon,
    AddonSearchItem,
    AssignRoleArg,
    CreateAddonInput,
    CreateSubscriptionInput,
    DatasetListItem,
    DatasetsPageResult,
    DatasetsQueryArg,
    DeleteAddonInput,
    EntitySearchArg,
    LegacyLoginResponse,
    LegacyLoginRequest,
    Subscription,
    SubscriptionsLookupResponse,
    SubscriptionsPageResult,
    SubscriptionsQueryArg,
    SupportPackageItem,
    SupportPackagesPageResult,
    SupportPackagesQueryArg,
    Tenant,
    UpdateAddonInput,
    UpdateSubscriptionInput,
    UserListItem,
    UserResponse,
    UsersLookupResponse,
    UsersPageResult,
    UsersQueryArg,
    ValidateResponse,
    VersionDrilldownPageResult,
    VersionDrilldownQueryArg,
    WorkspaceListItem,
    WorkspaceRole,
    WorkspaceRolesQueryArg,
    WorkspacesPageResult,
    WorkspacesQueryArg,
} from './queries';
import {
    activeVersionsInfiniteQuery,
    assignRoleMutation,
    changeSubscriptionStateMutation,
    createAddonMutation,
    createSubscriptionMutation,
    datasetsInfiniteQuery,
    deleteAddonMutation,
    legacyLoginMutation,
    searchAddonsQuery,
    searchDatasetsQuery,
    searchSubscriptionsQuery,
    searchSupportPackagesQuery,
    searchUsersQuery,
    searchWorkspacesQuery,
    signoutMutation,
    subscriptionByIdQuery,
    subscriptionsInfiniteQuery,
    subscriptionsLookupQuery,
    supportPackagesInfiniteQuery,
    tenantsQuery,
    updateAddonMutation,
    updateSubscriptionMutation,
    userQuery,
    usersInfiniteQuery,
    usersLookupQuery,
    validateQuery,
    versionDrilldownInfiniteQuery,
    workspaceRolesQuery,
    workspacesInfiniteQuery,
} from './queries';

export const getImagoMpApiUrl = () => {
    if (getIsProduction()) {
        return 'https://imago.api.seequent.com/';
    }

    return getApiEndpoint().URL;
};

const baseQuery: BaseQueryFn<string | FetchArgs, unknown> = async (args, api, extraOptions) => {
    const rawBaseQuery = fetchBaseQuery({
        baseUrl: getImagoMpApiUrl(),
        prepareHeaders: (headers) => {
            const combinedToken = getCombinedToken();
            headers.set('imago-api-token', combinedToken?.access_token ?? '');
            headers.set('Content-Type', 'application/json; charset=utf-8');
            return headers;
        },
    });

    return rawBaseQuery(args, api, extraOptions);
};

export const imagoMpApi = createApi({
    reducerPath: 'imagoMpApi',
    baseQuery,
    tagTypes: Object.values(ApiEndpointTags),
    endpoints: (builder) => ({
        legacyLogin: builder.mutation<LegacyLoginResponse, LegacyLoginRequest>(legacyLoginMutation),
        validate: builder.query<ValidateResponse, undefined>(validateQuery),
        getUser: builder.query<UserResponse, string>(userQuery),
        getSubscriptionsLookup: builder.query<SubscriptionsLookupResponse, void>(
            subscriptionsLookupQuery,
        ),
        getSubscriptions: builder.infiniteQuery<
            SubscriptionsPageResult,
            SubscriptionsQueryArg,
            { offset: number }
        >(subscriptionsInfiniteQuery),
        getSubscriptionById: builder.query<Subscription, string>(subscriptionByIdQuery),
        createSubscription: builder.mutation<Subscription, CreateSubscriptionInput>(
            createSubscriptionMutation,
        ),
        updateSubscription: builder.mutation<Subscription, UpdateSubscriptionInput>(
            updateSubscriptionMutation,
        ),
        createAddon: builder.mutation<Addon, CreateAddonInput>(createAddonMutation),
        updateAddon: builder.mutation<Addon, UpdateAddonInput>(updateAddonMutation),
        deleteAddon: builder.mutation<void, DeleteAddonInput>(deleteAddonMutation),
        getTenants: builder.query<Tenant[], void>(tenantsQuery),
        getUsersLookup: builder.query<UsersLookupResponse, void>(usersLookupQuery),
        searchAddons: builder.query<AddonSearchItem[], EntitySearchArg>(searchAddonsQuery),
        searchSubscriptions: builder.query<Subscription[], EntitySearchArg>(
            searchSubscriptionsQuery,
        ),
        searchUsers: builder.query<UserListItem[], EntitySearchArg>(searchUsersQuery),
        searchSupportPackages: builder.query<SupportPackageItem[], EntitySearchArg>(
            searchSupportPackagesQuery,
        ),
        searchWorkspaces: builder.query<WorkspaceListItem[], EntitySearchArg>(
            searchWorkspacesQuery,
        ),
        searchDatasets: builder.query<DatasetListItem[], EntitySearchArg>(searchDatasetsQuery),
        getUsers: builder.infiniteQuery<UsersPageResult, UsersQueryArg, { offset: number }>(
            usersInfiniteQuery,
        ),
        getWorkspaces: builder.infiniteQuery<
            WorkspacesPageResult,
            WorkspacesQueryArg,
            { offset: number }
        >(workspacesInfiniteQuery),
        getDatasets: builder.infiniteQuery<
            DatasetsPageResult,
            DatasetsQueryArg,
            { offset: number }
        >(datasetsInfiniteQuery),
        getSupportPackages: builder.infiniteQuery<
            SupportPackagesPageResult,
            SupportPackagesQueryArg,
            { offset: number }
        >(supportPackagesInfiniteQuery),
        getActiveVersions: builder.infiniteQuery<
            ActiveVersionsPageResult,
            ActiveVersionsQueryArg,
            { offset: number }
        >(activeVersionsInfiniteQuery),
        getVersionDrilldown: builder.infiniteQuery<
            VersionDrilldownPageResult,
            VersionDrilldownQueryArg,
            { offset: number }
        >(versionDrilldownInfiniteQuery),
        signout: builder.mutation<void, void>(signoutMutation),
        getWorkspaceRoles: builder.query<WorkspaceRole[], WorkspaceRolesQueryArg>(
            workspaceRolesQuery,
        ),
        assignRole: builder.mutation<WorkspaceRole[], AssignRoleArg>(assignRoleMutation),
        changeSubscriptionState: changeSubscriptionStateMutation(builder),
    }),
});

export const {
    useLegacyLoginMutation,
    useValidateQuery,
    useGetUserQuery,
    useGetSubscriptionsLookupQuery,
    useGetSubscriptionsInfiniteQuery,
    useGetSubscriptionByIdQuery,
    useCreateSubscriptionMutation,
    useUpdateSubscriptionMutation,
    useCreateAddonMutation,
    useUpdateAddonMutation,
    useDeleteAddonMutation,
    useGetTenantsQuery,
    useGetUsersLookupQuery,
    useSearchAddonsQuery,
    useSearchSubscriptionsQuery,
    useSearchUsersQuery,
    useSearchSupportPackagesQuery,
    useSearchWorkspacesQuery,
    useSearchDatasetsQuery,
    useGetUsersInfiniteQuery,
    useGetWorkspacesInfiniteQuery,
    useGetDatasetsInfiniteQuery,
    useGetSupportPackagesInfiniteQuery,
    useGetActiveVersionsInfiniteQuery,
    useGetVersionDrilldownInfiniteQuery,
    useSignoutMutation,
    useGetWorkspaceRolesQuery,
    useAssignRoleMutation,
    useChangeSubscriptionStateMutation,
} = imagoMpApi;
